import { z } from "zod";

// Raw provider stop/finish reason passed through verbatim (e.g. Anthropic "end_turn",
// OpenAI "stop", Google "STOP"). No normalization — consumers compare/display as-is.
export const FinishReasonSchema = z.string();
export type FinishReason = z.infer<typeof FinishReasonSchema>;

/**
 * Token usage payload attached to `step-end` and `message-end` events.
 *
 * Two-layer design:
 * - **Flat aggregate** (`promptTokens`, `completionTokens`, `totalTokens`, `totalCostUsd`, …):
 *   the turn-level totals across all models. This is the billing source of truth and drives
 *   `ModelUsageBillingService` and UI token counters.
 * - **`modelUsage` breakdown**: per-model detail, only populated by the Daytona / Anthropic
 *   Agent SDK adapter.
 *
 * Single-model invariant, on the TOKEN fields: when only one model is used, `modelUsage` has
 * exactly one entry whose token counts equal the flat aggregate. That equality is correct —
 * NOT a duplicate or bug. Multi-model runs (e.g. Cue dispatching a sub-agent on a
 * different model) produce 2+ entries whose token counts sum to the flat aggregate.
 *
 * The COST fields are scoped differently and do not stand in that relation. `totalCostUsd`
 * below is one turn's own cost, while a runner reports `costUSD` per model as a running total
 * for the whole runner session — so the flat cost is not the sum of the per-model costs, and
 * nothing should reconcile the two. Nothing populates `modelUsage` on the sandbox path today;
 * whoever wires it owes that field the same per-turn derivation the flat cost already gets.
 *
 * Consumers: billing and UI read the flat fields; per-model attribution (LLM tracing,
 * cost dashboards) reads `modelUsage`.
 */
/**
 * Whose consumption a set of token counts covers.
 *
 * A sandbox session runs a lead agent and may run background agents beside it,
 * and the counts on this shape have two producers that disagree about them: a
 * host accumulator folds a usage line for every assistant message, the lead's
 * and each agent's alike, while the provider's own end-of-generation figure
 * covers the lead's loop and leaves the agents out. The counts look identical
 * either way, so the producer says which it built.
 *
 * The two members spell the values the billing row stores, so one vocabulary
 * crosses from here to the column and no mapping table sits between them to
 * drift. That is checked rather than merely intended: the settle assigns a value
 * of this type to a field typed from the generated database enum, so renaming a
 * member on either side is a type error at that assignment rather than a value
 * the write path refuses at runtime.
 */
export const ChargedPopulationSchema = z.enum(["LEAD_ONLY", "LEAD_AND_AGENTS"]);

export type ChargedPopulation = z.infer<typeof ChargedPopulationSchema>;

export const ChatStreamTokenUsageSchema = z.object({
  promptTokens: z.number(),
  completionTokens: z.number(),
  totalTokens: z.number(),
  reasoningTokens: z.number().optional(),
  cacheReadInputTokens: z.number().optional(),
  cacheCreationInputTokens: z.number().optional(),
  cachedTokens: z.number().optional(),
  /**
   * Tokens written to the provider's prompt cache this turn. Set only by the
   * OpenAI adapter — a subset of `promptTokens`, disjoint from `cachedTokens`
   * (not additive to it). Distinct from `cacheCreationInputTokens` above,
   * which Anthropic's shape treats as additive to `promptTokens` rather than
   * a subset of it.
   */
  cacheWriteTokens: z.number().nonnegative().optional(),
  /**
   * What this turn cost, across all models. Only populated by the Daytona adapter.
   *
   * Derived rather than reported: a runner sends the agent SDK's running total for its whole
   * session, and the host subtracts the total already billed before the figure reaches this
   * shape. Absent means no cost is attributable to this turn — the reader prices it from the
   * token counts beside it rather than treating a missing cost as zero.
   */
  totalCostUsd: z.number().nonnegative().optional(),
  /**
   * Whose consumption the token counts above cover — see `ChargedPopulationSchema`.
   *
   * Host-side, like `totalCostUsd` beside it: the settle reads it to record what
   * a charge covered, and no browser reader consumes it. It rides this shape
   * because the producer that knows is the sandbox parser and the only channel
   * from there to the settle on the turn path is the event this usage sits on.
   *
   * Absent means unstated, never "the lead". A provider that cannot run
   * background agents leaves it off, and so does any producer that has not been
   * taught to say.
   */
  tokenPopulation: ChargedPopulationSchema.optional(),
  /**
   * Per-model token breakdown from the Anthropic Agent SDK. Only populated by the Daytona adapter.
   * Shape mirrors the SDK's raw record (uses `inputTokens`/`outputTokens`/`costUSD`, not the
   * Nexus-wide naming) by design. In a single-model run the sole entry equals the flat aggregate —
   * expected, not redundant. In a multi-model run entries sum to the flat aggregate.
   */
  modelUsage: z
    .record(
      z.string(),
      z.object({
        inputTokens: z.number(),
        outputTokens: z.number(),
        cacheReadInputTokens: z.number().optional(),
        cacheCreationInputTokens: z.number().optional(),
        costUSD: z.number(),
        webSearchRequests: z.number().int().nonnegative().optional(),
        contextWindow: z.number().int().nonnegative().optional(),
        maxOutputTokens: z.number().int().nonnegative().optional()
      })
    )
    .optional()
});

export type ChatStreamTokenUsage = z.infer<typeof ChatStreamTokenUsageSchema>;

/**
 * Request shape used when the frontend submits a tool execution result back to
 * continue an agentic turn.
 *
 * `status` — the decision: `"rejected"` when the user declined (no execution),
 * `"accepted"` when the tool ran (or will run server-side). The backend uses
 * this to distinguish a user decline from a tool that ran and produced an error.
 *
 * `isError` — accepted-but-failed: the tool ran but its apply step threw.
 * Only meaningful when `status === "accepted"`.
 *
 * `result` — stringified output (user's answer for ask_user_question, or the
 * decline/apply message for modify_prompt).
 */
export const ToolResultPayloadSchema = z.object({
  toolCallId: z.string(),
  name: z.string(),
  result: z.string(),
  status: z.enum(["accepted", "rejected"]).optional(),
  isError: z.boolean().optional(),
  // FE-authored reason for not-accepted results (rejected / skipped). The
  // frontend is the sole author because the backend cannot distinguish
  // reject from skip; the backend relays this verbatim.
  reason: z.string().optional()
});

export type ToolResultPayload = z.infer<typeof ToolResultPayloadSchema>;

/**
 * Verdict on a tool the backend *deferred* — gated server-side without running
 * it. Distinct from {@link ToolResultPayloadSchema}: a deferred tool has not
 * executed, so there is no `result` string to relay. The backend runs (or
 * skips) the tool on the continuation based on `approved`; both verdicts trigger
 * the continuation (on reject the model adapts).
 *
 * `message`     — optional FE-authored note relayed to the model alongside the
 *                 verdict (e.g. a reject rationale).
 * `input`       — the tool input captured when the call was deferred. On resume
 *                 the model re-issues calls with fresh ids, so when two verdicts
 *                 share a `toolName` the backend disambiguates by matching this
 *                 against the re-issued arguments.
 * `updatedInput`— optional edited tool input the user adjusted before approving;
 *                 the backend executes the tool with this in place of the
 *                 original arguments.
 *
 * Carried on the continuation `POST /stream` body field `toolVerdicts`,
 * never mixed into `toolResults`.
 */
export const ToolVerdictSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  approved: z.boolean(),
  message: z.string().optional(),
  input: z.record(z.string(), z.unknown()).optional(),
  updatedInput: z.record(z.string(), z.unknown()).optional()
});

export type ToolVerdict = z.infer<typeof ToolVerdictSchema>;
